import { ALLOW_PERSIST_TIKTOK_SETTINGS } from 'config'
import { TikTokPostProfileSettings, TikTokPrivacyOptions } from 'shared'

export const ALLOW_OPTION_KEYS = ['enableComment', 'enableDuet', 'enableStitch'] as const

export type AllowOptionKey = typeof ALLOW_OPTION_KEYS[number]

interface ReconcileParams {
  ppid: string
  options: TikTokPrivacyOptions
  /** Settings persisted server-side by "Remember this choice". */
  saved?: Partial<TikTokPostProfileSettings> | null
  /** Settings already held for the post being composed, if any. */
  current?: TikTokPostProfileSettings
  isPhotoPost?: boolean
  ignoreSavedSettings?: boolean
}

/** Values the legacy API uses for a flag that is off. Anything else non-empty means on. */
const FALSY_FLAG_VALUES = ['0', 'false']

/**
 * Duet and Stitch are video-only interactions. TikTok rejects/ignores them for photo posts, and keeping
 * them enabled leaks raw option keys into the "Allow" select label (PPSelect falls back to the value
 * when an option is missing). Strip them so the displayed and submitted settings always agree.
 */
export function withPostTypeConstraints(settings: TikTokPostProfileSettings, isPhotoPost: boolean): TikTokPostProfileSettings {
  if (!isPhotoPost || (!settings.enableDuet && !settings.enableStitch)) {
    return settings
  }
  return { ...settings, enableDuet: false, enableStitch: false }
}

/** A blank record. Used to tell the read path below that there is nothing to restore. */
export function emptyTikTokSettings(ppid: string): TikTokPostProfileSettings {
  return {
    ppid,
    privacyLevel: '',
    promoting: '',
    enableComment: false,
    enableDuet: false,
    enableStitch: false,
    autoAddMusic: false,
    isAiContent: false
  }
}

/**
 * The legacy API answers with booleans, 0/1 or "0"/"1". A missing key must stay `undefined`: `reconcileTikTokSettings`
 * relies on it to tell "saved as off" apart from "never saved", and a fabricated `false` would look like a real choice.
 */
export function toSavedFlag(value: unknown): boolean | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  return typeof value === 'string' ? !FALSY_FLAG_VALUES.includes(value.toLowerCase()) : Boolean(value)
}

/**
 * There is no endpoint to delete a saved record, so unchecking "Remember this choice" persists `emptyTikTokSettings`
 * instead. A record with nothing set is that blank marker (or a profile that never saved one) and must not override
 * anything - without this check its `false` flags would read as deliberate choices.
 */
export function hasSavedTikTokSettings(saved?: Partial<TikTokPostProfileSettings> | null): boolean {
  if (!saved) {
    return false
  }
  return Boolean(
    saved.privacyLevel || saved.promoting || saved.enableComment || saved.enableDuet ||
    saved.enableStitch || saved.autoAddMusic
  )
}

/** Maps the raw `get_settings` response onto the shape the composer works with. */
export function normalizeTikTokPrivacyOptions(raw: any): TikTokPrivacyOptions {
  return {
    privacyLevel: raw.privacyLevelOptions,
    enableComment: raw.enableComment.map(Boolean),
    enableDuet: raw.enableDuet.map(Boolean),
    enableStitch: raw.enableStitch.map(Boolean),
    maxDuration: raw.maxDuration,
    autoAddMusic: Boolean(raw.autoAddMusic),
    isAiContent: Boolean(raw.isAiContent)
  }
}

function pickAllowed<T>(allowed: T[], value: T | undefined, fallback: T): T {
  return value !== undefined && allowed.includes(value) ? value : fallback
}

/**
 * Merges the saved and in-progress settings, then drops anything the profile does not actually allow.
 * `privacyLevel` and `promoting` are deliberately left empty when there is nothing to restore: TikTok
 * requires both to be chosen explicitly, and the composer blocks publishing until they are.
 */
export function reconcileTikTokSettings({
  ppid, options, saved, current, isPhotoPost, ignoreSavedSettings
}: ReconcileParams): TikTokPostProfileSettings {
  const savedSettings = hasSavedTikTokSettings(saved) ? saved : undefined
  // `??`, not `||`: a saved flag turned off is a real choice and has to win over both the in-progress value and the
  // profile default below, otherwise "Remember this choice" silently switches the interaction back on.
  const preferred = ALLOW_PERSIST_TIKTOK_SETTINGS && !ignoreSavedSettings ? {
    privacyLevel: savedSettings?.privacyLevel || current?.privacyLevel,
    enableComment: savedSettings?.enableComment ?? current?.enableComment,
    enableDuet: savedSettings?.enableDuet ?? current?.enableDuet,
    enableStitch: savedSettings?.enableStitch ?? current?.enableStitch,
    // Intentionally not carried over from `current`: a promotion disclosure is re-affirmed per post.
    promoting: savedSettings?.promoting,
    autoAddMusic: savedSettings?.autoAddMusic ?? current?.autoAddMusic,
    // Never restored from the saved record: the AI flag belongs to the post, not to the profile
    isAiContent: current?.isAiContent
  } : current

  return withPostTypeConstraints({
    ppid,
    privacyLevel: pickAllowed(options.privacyLevel, preferred?.privacyLevel, ''),
    enableComment: pickAllowed(options.enableComment, preferred?.enableComment, options.enableComment[0] ?? false),
    enableDuet: pickAllowed(options.enableDuet, preferred?.enableDuet, options.enableDuet[0] ?? false),
    enableStitch: pickAllowed(options.enableStitch, preferred?.enableStitch, options.enableStitch[0] ?? false),
    promoting: preferred?.promoting || '',
    // Coerced, not passed through: a legacy 1/"1" would land in a field typed as boolean and be submitted as-is.
    autoAddMusic: Boolean(preferred?.autoAddMusic),
    isAiContent: Boolean(preferred?.isAiContent)
  }, Boolean(isPhotoPost))
}
